import { type Tenant, TenantSchema } from "@albertine/types/openapi/lmt";
import classNames from "classnames";
import { Plus } from "lucide-react";
import type React from "react";
import { formatTenantName } from "@/common/utils/tenant.util";
import { Label } from "@/components/ui/label";
import { MenuButton, type MenuButtonOptions } from "./Button/MenuButton";
import { Tabs } from "./Tabs";

export type TenantTabKey = "default" | Tenant;

type TenantTabsProps = {
    defaultTabText: string;
    keepAllTabsRendered?: boolean;
    tenantOptions: MenuButtonOptions;
    tabs: {
        key: TenantTabKey;
        children: React.ReactNode;
    }[];
    onTabClick?: (key: TenantTabKey) => void;
};

const allTenantTabKeys: TenantTabKey[] = ["default", ...TenantSchema.options];

function getTenantTabText(key: TenantTabKey, defaultTabText: string): string {
    return key === "default" ? defaultTabText : formatTenantName(key);
}

export function TenantTabs(props: TenantTabsProps) {
    const { defaultTabText, keepAllTabsRendered, tabs, tenantOptions, onTabClick } =
        props;

    const hasTenantOptions = tenantOptions.length !== 0;
    const showAddTenants = hasTenantOptions && allTenantTabKeys.length !== tabs.length;

    const tabsWithText = tabs.map((tab) => ({
        ...tab,
        text: getTenantTabText(tab.key, defaultTabText),
    }));

    return (
        <Tabs
            tabs={tabsWithText}
            defaultTab="default"
            keepAllTabsRendered={keepAllTabsRendered}
            onTabClick={onTabClick}
            tabBarClassName={classNames({
                "border-zinc-100! bg-zinc-100": hasTenantOptions,
            })}
            trailing={
                showAddTenants ? (
                    <MenuButton
                        options={tenantOptions}
                        placement="left"
                        button={
                            <span className="flex items-center gap-1">
                                <Plus className="size-4 text-emerald-600" />
                                <Label className="font-semibold text-emerald-600 leading-[18px]">
                                    Add tenant
                                </Label>
                            </span>
                        }
                    />
                ) : undefined
            }
        />
    );
}
